import { useState, useEffect, useCallback } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../../assets/logo/main-logo.png';
import {
  FaTh,
  FaMobileAlt,
  FaStar,
  FaHeart,
  FaComments,
  FaBell,
  FaPlug,
  FaUserCircle,
  FaTicketAlt,
  FaSignOutAlt,
  FaCog,
  FaFileAlt,
  FaBoxOpen,
} from 'react-icons/fa';

import { apiClient } from '../../../api';
import { ProfilePictureDisplay } from '../../components/ProfilePicture';
import {
  getProfilePictureUrlFromName,
  getUserProfileWithPicture,
} from '../../../service/profilePictureService';
import { useAuth } from '../../../context/AuthContext';
import { useChatUnread } from '../../../context/ChatUnreadContext';
import CustomButton from '../../components/CustomButton';
import { handleLogout } from '../../components/auth/apiAuth';

const CustomerSidebar = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { unreadMessageCount } = useChatUnread();
  const [userName, setUserName] = useState('User');
  const [profileImage, setProfileImage] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate('/');
  };

  // Get user ID from auth context
  const userId = auth.userId;

  // Fetch user data from API using the profile service
  const fetchUserData = useCallback(async () => {
    if (!userId || !auth.isAuthenticated) {
      console.log('Cannot fetch user data: No user ID or not authenticated');
      return;
    }

    try {
      setLoading(true);

      // Use the profile service to get user data with proper profile picture handling
      const profileData = await getUserProfileWithPicture(userId);

      if (profileData) {
        console.log('User profile data fetched for sidebar:', profileData);

        // Set user name from the profile data
        // setUserName(
        //   profileData.name || profileData.email?.split('@')[0] || 'User'
        // );
        setUserName(
          auth.firstName || auth.lastName
            ? `${auth.firstName ?? ''} ${auth.lastName ?? ''}`.trim()
            : profileData.firstName || profileData.lastName
              ? `${profileData.firstName ?? ''} ${profileData.lastName ?? ''}`.trim()
              : profileData.name || profileData.email?.split('@')[0] || 'User'
        );

        // Handle profile picture - prioritize profilePicture field
        const profileImageName =
          profileData.profileImage || profileData.profilePicture || '';
        if (profileImageName) {
          console.log('Found profile image name:', profileImageName);

          // If it's just an image name (not a full URL), convert it to full URL
          if (profileImageName && !profileImageName.startsWith('http')) {
            const fullUrl = getProfilePictureUrlFromName(profileImageName);
            setProfileImage(profileImageName); // Store the image name
            setProfileImageUrl(fullUrl); // Store the full URL
            console.log('Profile image URL generated:', fullUrl);
          } else {
            // If it's already a full URL, use it directly
            setProfileImage(profileImageName);
            setProfileImageUrl(profileImageName);
          }
        } else {
          // Clear profile image if none found
          setProfileImage('');
          setProfileImageUrl('');
        }
      }
    } catch (error) {
      console.error('Error fetching user data for sidebar:', error);
      // Fallback to direct API call if profile service fails
      try {
        const response = await apiClient.get(`user/api/v1/user/${userId}`);
        if (response.status === 200 && response.data) {
          const userData = response.data.user || response.data;
          setUserName(
            userData.firstName || userData.lastName
              ? `${userData.firstName ?? ''} ${userData.lastName ?? ''}`.trim()
              : userData.name || userData.email?.split('@')[0] || 'User'
          );

          // Handle profile picture from direct API response
          const profileImageData =
            userData.profilePicture || userData.profileImage || '';
          if (profileImageData && !profileImageData.startsWith('http')) {
            const fullUrl = getProfilePictureUrlFromName(profileImageData);
            setProfileImage(profileImageData);
            setProfileImageUrl(fullUrl);
          } else if (profileImageData) {
            setProfileImage(profileImageData);
            setProfileImageUrl(profileImageData);
          }
        }
      } catch (fallbackError) {
        console.error('Fallback API call also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  }, [auth.isAuthenticated, userId]);

  // Get user name from auth context and fetch user data
  useEffect(() => {
    if (!auth.loading) {
      if (auth.isAuthenticated && auth.user) {
        console.log('User profile in sidebar:', auth.user);

        // Try to get user's name from different possible profile fields
        const name = auth.user.name || auth.user.email?.split('@')[0] || 'User';

        console.log('Using name for sidebar:', name);
        setUserName(name);

        // Fetch user data including profile image
        fetchUserData();
      } else {
        console.log('User not authenticated in sidebar');
        setUserName(t('customer_sidebar.guest_user'));
        setProfileImage('');
        setProfileImageUrl('');
      }
    }
  }, [auth.loading, auth.isAuthenticated, auth.user, fetchUserData]);

  // Listen for profile picture updates from localStorage or custom events
  useEffect(() => {
    const handleProfilePictureUpdate = () => {
      console.log('Profile picture update detected, refreshing sidebar data');
      fetchUserData();
    };

    // Listen for custom profile picture update events
    window.addEventListener(
      'profilePictureUpdated',
      handleProfilePictureUpdate
    );

    // Also listen for storage events (if profile updates are stored in localStorage)
    window.addEventListener('storage', (e) => {
      if (e.key === 'profilePictureUpdated') {
        handleProfilePictureUpdate();
      }
    });

    return () => {
      window.removeEventListener(
        'profilePictureUpdated',
        handleProfilePictureUpdate
      );
      window.removeEventListener('storage', handleProfilePictureUpdate);
    };
  }, [fetchUserData]);

  return (
    <nav className="bg-[#0f1419] border-r border-gray-200 h-screen fixed top-0 left-0 w-[260px] py-5 px-3 overflow-auto text-slate-700 transform -translate-x-full laptop:translate-x-0 transition-all duration-300 ease-in-out z-50">
      <div className="sticky -top-5 z-50 bg-[#0f1419] ">
        <div className="flex flex-col justify-center items-center ">
          <Link to="/">
            <img
              src={logo}
              alt={t('customer_sidebar.logo_alt')}
              className="object-contain h-16 w-auto"
            />
          </Link>
          <p className="text-[11px] text-gray-400 tracking-widest uppercase mt-3">
            {t('customer_sidebar.customer_dashboard')}
          </p>
          {/* <p className="text-[10px] text-gray-500 text-center mt-2 px-2 italic">
            One Platform. Every Service. Every Neighborhood.
          </p> */}
        </div>
      </div>

      <hr className="mb-6 mt-3 border-gray-600" />

      <div>
        <h4 className="text-[11px] uppercase tracking-wide text-slate-500 mb-3 px-3">
          {t('customer_sidebar.general')}
        </h4>
        <ul className="space-y-1 px-1 flex-1">
          <SidebarItem
            link="/customer/customer-dashboard"
            icon={<FaTh />}
            text={t('customer_sidebar.dashboard')}
          />
          <SidebarItem
            link="/customer/customer-booking"
            icon={<FaMobileAlt />}
            text={t('customer_sidebar.bookings')}
          />

          {/* Phase 2 — Quote-based booking */}
          <SidebarItem
            link="/customer/customer-job-requests"
            icon={<FaFileAlt />}
            text={t('customer_sidebar.job_requests')}
          />

          {/* Phase 3 — Package-based booking */}
          <SidebarItem
            link="/customer/customer-packages"
            icon={<FaBoxOpen />}
            text={t('customer_sidebar.my_packages')}
          />

          <SidebarItem
            link="/customer/customer-reviews"
            icon={<FaStar />}
            text={t('customer_sidebar.reviews')}
          />
          <SidebarItem
            link="/customer/customer-favorite-services"
            icon={<FaHeart />}
            text={t('customer_sidebar.favorite_services')}
          />
          <SidebarItem
            link="/customer/customer-chat"
            icon={<FaComments />}
            text={t('customer_sidebar.chat')}
            badge={unreadMessageCount}
          />
          <SidebarItem
            link="/customer/customer-notifications"
            icon={<FaBell />}
            text={t('customer_sidebar.notifications')}
          />
          <SidebarItem
            link="/customer/customer-support-tickets"
            icon={<FaTicketAlt />}
            text={t('customer_sidebar.support_tickets')}
          />
        </ul>
      </div>

      <hr className="my-6 border-gray-600" />

      <div>
        <h4 className="text-[11px] uppercase tracking-wide text-slate-500 mb-3 px-3">
          {t('customer_sidebar.settings')}
        </h4>
        <ul className="space-y-1 px-1 flex-1">
          <SidebarItem
            link="/customer/customer-profile"
            icon={<FaCog />}
            text={t('customer_sidebar.account_settings')}
          />
          <SidebarItem
            link="/customer/settings/connected-apps"
            icon={<FaPlug />}
            text={t('customer_sidebar.connected_apps')}
          />
        </ul>
      </div>

      <div className="mt-4">
        {/* <ul className="space-y-1 px-1">
          <SidebarItem
            link="/authentication/login"
            icon={<FaSignOutAlt />}
            text="Home"
          />
        </ul> */}

        <div
          className="flex flex-wrap items-center cursor-pointer border-t border-gray-600 py-3 mt-6 hover:bg-white/5 rounded-lg transition-colors px-2 -mx-2"
          onClick={() => navigate('/customer/customer-profile')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ')
              navigate('/customer/customer-profile');
          }}
        >
          <div className="w-10 h-10 rounded-md overflow-hidden">
            {loading ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-200 animate-pulse">
                <FaUserCircle className="text-gray-400 text-2xl" />
              </div>
            ) : (
              <>
                <ProfilePictureDisplay
                  profileImage={profileImage}
                  profileImageUrl={profileImageUrl}
                  userName={userName}
                  size="sm"
                  className="w-full h-full"
                />
              </>
            )}
          </div>

          <div className="ml-4">
            <p className="text-sm text-gray-200 font-medium">{userName}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {t('customer_sidebar.active_account')}
            </p>
          </div>
        </div>
        <div className="hidden laptop:block border-t border-gray-600 pb-2">
          <CustomButton
            label={t('customer_sidebar.home')}
            variant="flat"
            color="primary"
            fullWidth
            endContent={<FaSignOutAlt />}
            className="mt-5"
            onPress={handleNavigate}
          />

          <div className="px-2 pb-4 text-center space-y-2 mt-5">
            <p className="text-[11px] text-gray-500 font-medium leading-relaxed flex items-baseline gap-1">
              <span className="inline-flex">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 30 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M30 0H0V20H30V0Z" fill="#F0F0F0" />
                  <path d="M10 0H0V20H10V0Z" fill="#D80027" />
                  <path d="M30 0H20V20H30V0Z" fill="#D80027" />
                  <path
                    d="M16.875 11.3889L18.75 10.4514L17.8125 9.98275V9.04526L15.9375 9.98275L16.875 8.10777H15.9375L15 6.70148L14.0625 8.10777H13.125L14.0625 9.98275L12.1875 9.04526V9.98275L11.25 10.4514L13.125 11.3889L12.6563 12.3264H14.5313V13.7327H15.4688V12.3264H17.3438L16.875 11.3889Z"
                    fill="#D80027"
                  />
                </svg>
              </span>

              <span>{t('customer_sidebar.proudly_canadian')}</span>
            </p>
          </div>
        </div>

        <CustomButton
          label="Sign Out"
          variant="flat"
          color="danger"
          fullWidth
          endContent={<FaSignOutAlt />}
          className="mt-5"
          onPress={handleLogout}
        />
      </div>
    </nav>
  );
};

// Reusable Sidebar Item Component
interface SidebarItemProps {
  link: string;
  icon?: React.ReactNode;
  text: string;
  modalTarget?: string;
  badge?: number;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  link,
  icon,
  text,
  modalTarget,
  badge,
}) => {
  return (
    <li>
      <NavLink
        to={link}
        className={({ isActive }) =>
          `flex items-center gap-3 text-sm px-3 py-2 rounded-md transition-colors duration-200  ${
            isActive
              ? 'bg-primary/20 text-white'
              : 'text-gray-400 hover:bg-white/5 hover:text-white'
          }`
        }
        {...(modalTarget
          ? { 'data-bs-toggle': 'modal', 'data-bs-target': modalTarget }
          : {})}
      >
        {({ isActive }) => (
          <>
            {icon && (
              <span
                className={`text-lg ${
                  isActive ? 'text-secondary' : 'text-gray-400'
                }`}
              >
                {icon}
              </span>
            )}
            <span>{text}</span>
            {badge != null && badge > 0 && (
              <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-tight">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
};

export default CustomerSidebar;
